/**
 * KRA Photo Journal – Filter persistence
 *
 * Persists FilterBar state across reloads and shares it via URL:
 *   1. On mount → URL params (priority) → localStorage (fallback) → defaults
 *   2. On change → write back to both URL (replaceState) + localStorage
 *
 * Lets users:
 *   - Share a filtered view by copying the URL
 *   - Refresh the page without losing the active filter set
 *   - Open a bare URL and still recover the last session's filters
 *
 * URL params (kept short to stay shareable):
 *   p     → photographer name
 *   sf    → sort field    (returnDate | shootDate)
 *   sd    → sort dir      (asc | desc)
 *   q     → search query
 *   qf    → quick filter  (all | liked | downloaded | not-downloaded)
 *   tags  → tag filters   (groupKey:val1,val2|otherGroup:val3)
 *   m     → month filter  (YYYY-MM)
 */

import type { SortField, SortDir } from "./usePhotos.js";
import type { TagFilters } from "../lib/tagHelpers.js";
import type { QuickFilter } from "../App.js";

export interface PersistedFilters {
  selectedPhotographer: string | null;
  sortField: SortField;
  sortDir: SortDir;
  search: string;
  quickFilter: QuickFilter;
  tagFilters: TagFilters;
  monthFilter: string | null;
}

const DEFAULTS: PersistedFilters = {
  selectedPhotographer: null,
  sortField: "returnDate",
  sortDir: "desc",
  search: "",
  quickFilter: "all",
  tagFilters: {},
  monthFilter: null,
};

const STORAGE_KEY = "kra_pj_filters_v1";

const URL_KEYS = ["p", "sf", "sd", "q", "qf", "tags", "m"] as const;

// ── Tag filter encoding ────────────────────────────────────────────────────
// "kimonoTags:furisode,sensoji|sceneTags:sensoji"
function encodeTagFilters(t: TagFilters): string {
  const parts: string[] = [];
  for (const [group, vals] of Object.entries(t)) {
    if (!vals || vals.length === 0) continue;
    parts.push(`${group}:${vals.map(encodeURIComponent).join(",")}`);
  }
  return parts.join("|");
}

function decodeTagFilters(s: string): TagFilters {
  const result: TagFilters = {};
  for (const segment of s.split("|")) {
    const [group, vals] = segment.split(":");
    if (!group || !vals) continue;
    const list = vals.split(",").map(decodeURIComponent).filter(Boolean);
    if (list.length > 0) (result as Record<string, string[]>)[group] = list;
  }
  return result;
}

// ── Validators (defensive — URL/localStorage can contain garbage) ──────────
function isSortField(v: unknown): v is SortField {
  return v === "returnDate" || v === "shootDate";
}
function isSortDir(v: unknown): v is SortDir {
  return v === "asc" || v === "desc";
}
function isQuickFilter(v: unknown): v is QuickFilter {
  return v === "all" || v === "liked" || v === "downloaded" || v === "not-downloaded";
}

// ── Readers ────────────────────────────────────────────────────────────────
function readFromUrl(): Partial<PersistedFilters> | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const present = URL_KEYS.some((k) => url.searchParams.has(k));
  if (!present) return null;

  const out: Partial<PersistedFilters> = {};
  const p = url.searchParams.get("p");
  if (p) out.selectedPhotographer = p;
  const sf = url.searchParams.get("sf");
  if (isSortField(sf)) out.sortField = sf;
  const sd = url.searchParams.get("sd");
  if (isSortDir(sd)) out.sortDir = sd;
  const q = url.searchParams.get("q");
  if (q) out.search = q;
  const qf = url.searchParams.get("qf");
  if (isQuickFilter(qf)) out.quickFilter = qf;
  const tags = url.searchParams.get("tags");
  if (tags) out.tagFilters = decodeTagFilters(tags);
  const m = url.searchParams.get("m");
  if (m) out.monthFilter = m;
  return out;
}

function readFromStorage(): Partial<PersistedFilters> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedFilters>;
    // Coerce/validate fields so a stale or hand-edited blob can't crash the app
    const out: Partial<PersistedFilters> = {};
    if (typeof parsed.selectedPhotographer === "string" || parsed.selectedPhotographer === null) {
      out.selectedPhotographer = parsed.selectedPhotographer;
    }
    if (isSortField(parsed.sortField)) out.sortField = parsed.sortField;
    if (isSortDir(parsed.sortDir)) out.sortDir = parsed.sortDir;
    if (typeof parsed.search === "string") out.search = parsed.search;
    if (isQuickFilter(parsed.quickFilter)) out.quickFilter = parsed.quickFilter;
    if (parsed.tagFilters && typeof parsed.tagFilters === "object") out.tagFilters = parsed.tagFilters;
    if (typeof parsed.monthFilter === "string" || parsed.monthFilter === null) {
      out.monthFilter = parsed.monthFilter;
    }
    return out;
  } catch {
    return null;
  }
}

// ── Writers ────────────────────────────────────────────────────────────────
function writeToUrl(state: PersistedFilters) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  // Clear our keys first so removed filters drop out of the URL
  for (const k of URL_KEYS) url.searchParams.delete(k);

  if (state.selectedPhotographer) url.searchParams.set("p", state.selectedPhotographer);
  if (state.sortField !== DEFAULTS.sortField) url.searchParams.set("sf", state.sortField);
  if (state.sortDir !== DEFAULTS.sortDir) url.searchParams.set("sd", state.sortDir);
  if (state.search) url.searchParams.set("q", state.search);
  if (state.quickFilter !== DEFAULTS.quickFilter) url.searchParams.set("qf", state.quickFilter);
  const tags = encodeTagFilters(state.tagFilters);
  if (tags) url.searchParams.set("tags", tags);
  if (state.monthFilter) url.searchParams.set("m", state.monthFilter);

  // replaceState — don't pollute browser history with every keystroke
  window.history.replaceState({}, "", url.toString());
}

function writeToStorage(state: PersistedFilters) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota exhausted or storage disabled — silently ignore */
  }
}

/**
 * One-time read at mount: URL → localStorage → defaults.
 * Pass this function to `useState(loadInitialFilters)` to seed state.
 */
export function loadInitialFilters(): PersistedFilters {
  const fromUrl = readFromUrl();
  if (fromUrl) return { ...DEFAULTS, ...fromUrl };
  const fromStorage = readFromStorage();
  if (fromStorage) return { ...DEFAULTS, ...fromStorage };
  return DEFAULTS;
}

/**
 * Sync state to URL (replaceState) and localStorage. Call from a useEffect
 * that depends on every persisted field.
 */
export function syncFilters(state: PersistedFilters) {
  writeToUrl(state);
  writeToStorage(state);
}
